// Which of a project's sources are set up, read from the same queries the
// Settings cards render so the tags can never disagree with the cards below them.
import { useProject } from "../api";
import type { DriveSyncState } from "../api/hooks/useDriveSync";
import { useProjectMailboxes } from "../api/hooks/useProjectMailboxes";
import { useProjectSlack } from "../api/hooks/useSlack";

export type SetupState = "complete" | "incomplete";

export type SetupStepId = "drive" | "email" | "slack";

// The Settings section each step is completed in, which the banner scrolls to.
export const setupSectionId = (id: SetupStepId) => `settings-${id}`;

export interface SetupStep {
  id: SetupStepId;
  label: string;
  state: SetupState;
}

// A step is absent while its data loads, so nothing flashes "incomplete" on a
// project that is set up. Slack is absent where the Slack app is not installed:
// there is nothing the operator could do about it.
export const useProjectSetupSteps = (projectId: string): SetupStep[] => {
  const project = useProject(projectId || undefined).data;
  const mailboxes = useProjectMailboxes(projectId).data;
  const slack = useProjectSlack(projectId).data;

  const steps: SetupStep[] = [];
  if (project) {
    const drive = (project.drive_sync ?? null) as DriveSyncState | null;
    const driveDone = Boolean(drive && (drive.roots.length > 0 || drive.files.length > 0));
    steps.push({
      id: "drive",
      label: "Connect Google Drive",
      state: driveDone ? "complete" : "incomplete",
    });
  }
  if (project && mailboxes) {
    const emailDone =
      mailboxes.mailboxes.some((m) => m.status === "connected") &&
      (project.email_project_names ?? []).length > 0;
    steps.push({
      id: "email",
      label: "Connect email",
      state: emailDone ? "complete" : "incomplete",
    });
  }
  if (slack && (slack.configured || slack.workspace)) {
    steps.push({
      id: "slack",
      label: "Connect a Slack channel",
      state: slack.subscriptions.length > 0 ? "complete" : "incomplete",
    });
  }
  return steps;
};
